import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatNok } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, Star, UploadCloud, Loader2, Pencil, ExternalLink, Archive, Eye, EyeOff, X } from "lucide-react";
import { inputCls } from "../site/formPrimitives";
import { SHOP_CATEGORIES, AVAIL_STYLE, SPEC_LABELS } from "../shop/ShopLayout";

const STATUS_STYLE = { utkast: "bg-slate-100 text-ink/70", publisert: "bg-green-100 text-green-700", arkivert: "bg-slate-200 text-ink/60" };
const emptyProduct = {
  name: "", category: "Telefoner", price: "", description: "", condition: "", availability: "Tilgjengelig",
  status: "utkast", stock: 1, images: [],
  specs: { model: "", storage: "", color: "", battery_health: "", repairs_info: "", compatible_models: "" },
};

function L({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-navy">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-ink/50">{hint}</span>}
    </label>
  );
}

function ImagesEditor({ images, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const ids = [];
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      } catch (err) { toast.error(`${f.name}: ${err.response?.data?.detail || "opplasting feilet"}`); }
    }
    onChange([...images, ...ids]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };
  const move = (i, d) => { const a = [...images]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  const setCover = (i) => { const a = [...images]; const [x] = a.splice(i, 1); onChange([x, ...a]); };
  return (
    <div>
      <span className="mb-1 block text-xs font-semibold text-navy">Bilder <span className="font-normal text-ink/50">(første bilde er forsidebilde)</span></span>
      <div className="flex flex-wrap gap-3">
        {images.map((id, i) => (
          <div key={id} className="relative h-24 w-24 overflow-hidden rounded-lg border border-hairline bg-slate-50" data-testid={`product-image-${i}`}>
            <img src={fileUrl(id)} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute left-1 top-1 rounded bg-warm px-1.5 py-0.5 text-[10px] font-bold text-navy">Forside</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-navy/80 px-1 py-0.5 text-white">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30" title="Flytt venstre" data-testid={`product-image-left-${i}`}><ChevronLeft className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => setCover(i)} disabled={i === 0} className="disabled:opacity-30" title="Sett som forside" data-testid={`product-image-cover-${i}`}><Star className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => onChange(images.filter((_, k) => k !== i))} title="Slett" data-testid={`product-image-delete-${i}`}><X className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="disabled:opacity-30" title="Flytt høyre" data-testid={`product-image-right-${i}`}><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-hairline text-xs text-ink/60 hover:border-electric hover:text-electric" data-testid="product-image-upload">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />} {uploading ? "Laster ..." : "Last opp"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="product-image-input" />
      </div>
    </div>
  );
}

function ProductForm({ initial, onSaved, onCancel }) {
  const [p, setP] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setSpec = (k, v) => setP((x) => ({ ...x, specs: { ...x.specs, [k]: v } }));
  const isPhone = p.category === "Telefoner";

  const save = async (e) => {
    e.preventDefault();
    if (!p.name.trim()) return toast.error("Navn må fylles ut");
    if (p.price === "" || Number(p.price) < 0) return toast.error("Pris må fylles ut");
    setSaving(true);
    try {
      const body = { ...p, price: Number(p.price), stock: Number(p.stock) };
      const r = p.id ? await api.put(`/admin/products/${p.id}`, body) : await api.post("/admin/products", body);
      toast.success(p.id ? "Produkt oppdatert" : "Produkt opprettet");
      onSaved(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Kunne ikke lagre produktet");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="rounded-xl border border-electric/30 bg-white p-4 sm:p-5 space-y-4" data-testid="product-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <L label="Produktnavn *"><input className={inputCls} value={p.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" /></L>
        <L label="Kategori *">
          <select className={inputCls} value={p.category} onChange={(e) => set("category", e.target.value)} data-testid="product-category-input">
            {SHOP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </L>
        <L label="Pris (kr) *"><input type="number" min="0" className={inputCls} value={p.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" /></L>
        <L label="Tilstand" hint="F.eks. Som ny, Meget god, God"><input className={inputCls} value={p.condition} onChange={(e) => set("condition", e.target.value)} data-testid="product-condition-input" /></L>
        <L label="Tilgjengelighet">
          <select className={inputCls} value={p.availability} onChange={(e) => set("availability", e.target.value)} data-testid="product-availability-input">
            {Object.keys(AVAIL_STYLE).map((a) => <option key={a}>{a}</option>)}
          </select>
        </L>
        <L label="Antall på lager"><input type="number" min="0" className={inputCls} value={p.stock} onChange={(e) => set("stock", e.target.value)} data-testid="product-stock-input" /></L>
        <L label="Status">
          <select className={inputCls} value={p.status} onChange={(e) => set("status", e.target.value)} data-testid="product-status-input">
            <option value="utkast">Utkast (skjult)</option><option value="publisert">Publisert</option><option value="arkivert">Arkivert</option>
          </select>
        </L>
      </div>
      <L label="Beskrivelse"><textarea rows={4} className={`${inputCls} resize-y`} value={p.description} onChange={(e) => set("description", e.target.value)} data-testid="product-description-input" /></L>
      <div>
        <span className="mb-1 block text-xs font-semibold text-navy">Spesifikasjoner <span className="font-normal text-ink/50">(kun utfylte felt vises)</span></span>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {isPhone ? (
            <>
              <L label={SPEC_LABELS.model}><input className={inputCls} value={p.specs.model} onChange={(e) => setSpec("model", e.target.value)} data-testid="product-spec-model" /></L>
              <L label={SPEC_LABELS.storage}><input className={inputCls} value={p.specs.storage} onChange={(e) => setSpec("storage", e.target.value)} placeholder="F.eks. 128 GB" data-testid="product-spec-storage" /></L>
              <L label={SPEC_LABELS.color}><input className={inputCls} value={p.specs.color} onChange={(e) => setSpec("color", e.target.value)} data-testid="product-spec-color" /></L>
              <L label={SPEC_LABELS.battery_health}><input className={inputCls} value={p.specs.battery_health} onChange={(e) => setSpec("battery_health", e.target.value)} placeholder="F.eks. 91 %" data-testid="product-spec-battery" /></L>
              <div className="sm:col-span-2"><L label={SPEC_LABELS.repairs_info} hint="Reparasjoner, byttede deler eller kjente feil"><textarea rows={2} className={`${inputCls} resize-y`} value={p.specs.repairs_info} onChange={(e) => setSpec("repairs_info", e.target.value)} data-testid="product-spec-repairs" /></L></div>
            </>
          ) : (
            <div className="sm:col-span-2"><L label={SPEC_LABELS.compatible_models} hint="F.eks. iPhone 15, iPhone 15 Pro"><input className={inputCls} value={p.specs.compatible_models} onChange={(e) => setSpec("compatible_models", e.target.value)} data-testid="product-spec-compatible" /></L></div>
          )}
        </div>
      </div>
      <ImagesEditor images={p.images} onChange={(imgs) => set("images", imgs)} />
      <div className="flex flex-wrap justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="rounded-full border border-hairline px-4 py-2 text-sm font-medium text-navy" data-testid="product-cancel">Avbryt</button>
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="product-save">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Lagre produkt
        </button>
      </div>
    </form>
  );
}

export default function ProductsAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("aktive");

  const load = () => api.get("/admin/products").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (id, body, msg) => {
    try {
      const r = await api.patch(`/admin/products/${id}`, body);
      setRows((rs) => rs.map((x) => (x.id === id ? r.data : x)));
      toast.success(msg);
    } catch { toast.error("Kunne ikke oppdatere"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Slette produktet permanent? Bruk heller «Arkiver» om du vil skjule det.")) return;
    await api.delete(`/admin/products/${id}`);
    setRows((rs) => rs.filter((x) => x.id !== id));
    toast.success("Produkt slettet");
  };

  const visible = rows.filter((r) => filter === "alle" ? true : filter === "arkiverte" ? r.status === "arkivert" : r.status !== "arkivert");

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Produkter</h1>
          <p className="mt-1 text-sm text-ink/60">{rows.filter((r) => r.status === "publisert").length} publisert · {rows.length} totalt</p>
        </div>
        <button onClick={() => setEditing({ ...emptyProduct })} className="inline-flex items-center gap-2 rounded-full bg-warm px-4 py-2 text-sm font-semibold text-navy" data-testid="product-new">
          <Plus className="h-4 w-4" /> Nytt produkt
        </button>
      </div>

      {editing && !editing.id && (
        <div className="mt-5"><ProductForm initial={editing} onSaved={(p) => { setRows((rs) => [p, ...rs]); setEditing(null); }} onCancel={() => setEditing(null)} /></div>
      )}

      <div className="mt-5 flex gap-2">
        {[["aktive", "Aktive"], ["arkiverte", "Arkiverte"], ["alle", "Alle"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs font-medium border ${filter === k ? "border-navy bg-navy text-white" : "border-hairline text-navy"}`} data-testid={`product-filter-${k}`}>{l}</button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {visible.length === 0 && <p className="text-sm text-ink/50">Ingen produkter her ennå. Klikk «Nytt produkt» for å legge til ditt første.</p>}
        {visible.map((r) => (
          <div key={r.id} className="rounded-xl border border-hairline bg-white shadow-sm" data-testid={`product-row-${r.id}`}>
            <div className="flex items-center gap-3 p-3 sm:p-4">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                {r.images?.[0] && <img src={fileUrl(r.images[0])} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-navy">{r.name}</p>
                <p className="truncate text-xs text-ink/60">{r.category} · {formatNok(r.price)} · Lager: {r.stock}{r.images?.length ? ` · ${r.images.length} bilder` : ""}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[r.status]}`} data-testid="product-row-status">{r.status}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${AVAIL_STYLE[r.availability]}`} data-testid="product-row-availability">{r.availability}</span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center">
                {r.status === "publisert" ? (
                  <button onClick={() => patch(r.id, { status: "utkast" }, "Avpublisert")} className="inline-flex items-center gap-1 rounded-full border border-hairline px-2.5 py-1 text-xs text-navy hover:border-electric" data-testid="product-unpublish"><EyeOff className="h-3.5 w-3.5" /> Avpubliser</button>
                ) : (
                  <button onClick={() => patch(r.id, { status: "publisert" }, "Publisert")} className="inline-flex items-center gap-1 rounded-full bg-electric px-2.5 py-1 text-xs font-medium text-white" data-testid="product-publish"><Eye className="h-3.5 w-3.5" /> Publiser</button>
                )}
                <button onClick={() => setEditing(editing?.id === r.id ? null : r)} className="inline-flex items-center gap-1 rounded-full border border-hairline px-2.5 py-1 text-xs text-navy hover:border-electric" data-testid="product-edit"><Pencil className="h-3.5 w-3.5" /> Rediger</button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-hairline px-3 py-2 text-xs sm:px-4">
              <span className="text-ink/50">Tilgjengelighet:</span>
              {Object.keys(AVAIL_STYLE).map((a) => (
                <button key={a} onClick={() => patch(r.id, { availability: a }, `Satt til ${a}`)} className={`rounded-full border px-2.5 py-0.5 font-medium ${r.availability === a ? "border-electric bg-electric text-white" : "border-hairline text-navy hover:border-electric"}`} data-testid={`product-avail-${a}`}>{a}</button>
              ))}
              <span className="ml-auto flex items-center gap-2">
                {r.status === "publisert" && <a href={`/butikk/${r.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-electric hover:underline" data-testid="product-view"><ExternalLink className="h-3.5 w-3.5" /> Vis</a>}
                {r.status !== "arkivert" ? (
                  <button onClick={() => patch(r.id, { status: "arkivert" }, "Arkivert")} className="inline-flex items-center gap-1 text-ink/60 hover:text-navy" data-testid="product-archive"><Archive className="h-3.5 w-3.5" /> Arkiver</button>
                ) : (
                  <button onClick={() => patch(r.id, { status: "utkast" }, "Gjenopprettet som utkast")} className="inline-flex items-center gap-1 text-ink/60 hover:text-navy" data-testid="product-unarchive">Gjenopprett</button>
                )}
                <button onClick={() => remove(r.id)} className="inline-flex items-center gap-1 text-red-600 hover:underline" data-testid="product-delete"><Trash2 className="h-3.5 w-3.5" /> Slett</button>
              </span>
            </div>
            {editing?.id === r.id && (
              <div className="border-t border-hairline p-3 sm:p-4">
                <ProductForm initial={{ ...emptyProduct, ...r, specs: { ...emptyProduct.specs, ...(r.specs || {}) } }} onSaved={(p) => { setRows((rs) => rs.map((x) => (x.id === p.id ? p : x))); setEditing(null); }} onCancel={() => setEditing(null)} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
